import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Col, Row, Form, Tab, Card, Nav } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import ConditionalButton from "../../../layout/conditional-button";
import ConditionalDropdown from "../../../layout/conditional-dropdown";
import { GENERATOR_STYLES } from "../../../layout/select/report-list";
import { isGuid, sanitizeFilename } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";
import { deepEqual } from "../../../utils/object";
import {
    style as renderingStyle,
    download as renderedDownload,
    fileName as renderingFileName,
    generators as renderingGenerators
} from "../../../utils/rendering";
import { applyCustomValues, getTemplateCustomFields } from "../../../utils/template-custom-fields";
import { firmSignatureRecipient, templateFirmSettings } from "../../../utils/firm";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getMatter, save as saveMatter, destroy as deleteMatter, completeExtract } from "../../../actions/matters";
import { get as getSettings } from "../../../actions/settings";
import { get as getClient } from "../../../actions/clients";
import { flagged as getReports, render, inline, prepareSignature } from "../../../actions/report.template";
import { flagged as getEmails, send as sendEmail } from "../../../actions/email.template";
import { get as getLogs, save as saveLog } from "../../../actions/logs";
import { createAgreement, getAgreementTypes, listAgreements } from "../../../actions/agreement";

import NavItem from "../../../layout/tabs/item";
import { Destructable } from "../../../layout/tabs";
import Notes from "../../../layout/logs";
import Calendar from "../../../layout/calendar";
import NavigationGuard from "../../../layout/navigation-guard";
import ErrorModal from "../../../layout/modal";
import ConfirmModal from "../../../layout/modal";
import EmailComposeModal from "../../../layout/email-compose-modal";
import ReportModal from "./modals/report";
import ExtractModal from "./modals/extract";
import DeleteMatterModal from "./modals/delete";

import General from "./general";
import Assignment from "./assignment";
import Parties from "./parties";
import Divorce from "../divorce";
import NameChange from "../name-change";
import Files from "../../files";
import Agreements from "./agreements";

import {
  ADD_AGREEMENT_PROVISION_LIBRARY,
  APPROVE_AGREEMENT_PROVISION_LIBRARY,
  CREATE_AGREEMENT,
  CUSTOMIZE_AGREEMENT_AI,
  EDIT_AGREEMENT,
  EXPORT_AGREEMENT,
  FINALIZE_AGREEMENT,
  VIEW_AGREEMENT,
  VIEW_AGREEMENT_PROVISION_LIBRARY,
  EDIT_MATTER
} from "../../../constants/permissions";

export const TABS = {
  DIVORCE: 1,
  CUSTODY: 2,
  CHILD_SUPPORT: 4,
  SPOUSAL_SUPPORT: 8,
  CIVIL: 16,
  CRIMINAL: 32,
  TRAFFIC: 64,
  PERSONAL_INJURY: 128,
  NAME_CHANGE: 256,
};

const COMPLETE_EXTRACT_OPTION = {
    uuid: "complete-matter-extract",
    caption: "Export",
    name: "COMPLETE_EXTRACT",
    category: "Extracts",
    style: GENERATOR_STYLES.EXTRACT
};

function personDisplayName(person = {}) {
    return (
        person.displayName ||
        person.name ||
        [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ") ||
        person.email ||
        ""
    ).trim();
}

function agreementTabKey(uuid) {
    return `agreement:${uuid}`;
}

function agreementTabCaption(matterCaption, agreement = {}) {
    const matterTitle = String(matterCaption || "").trim();
    const agreementTitle = String(agreement.title || agreement.name || "Agreement")
        .trim()
        .replace(/\s+[—–]\s+/g, " - ");

    if (!matterTitle) return agreementTitle;
    if (agreementTitle.toLowerCase().startsWith(matterTitle.toLowerCase())) return agreementTitle;
    return `${matterTitle} - ${agreementTitle}`;
}

function matterSignatureRecipients(matter = {}, firm = {}) {
    const person = matter?.Client?.Person || {};
    const email = String(person.email || "").trim();
    const recipients = [];

    if (email) {
        recipients.push({
            key: "client",
            name: personDisplayName(person) || email,
            email,
            role: "client",
            personUuid: person.uuid,
        });
    }

    const firmRecipient = firmSignatureRecipient(firm);
    if (firmRecipient) recipients.push(firmRecipient);

    return recipients;
}

function signatureRequestTemplateData(enabled) {
    if (!enabled) return {};

    return {
        prepare: true,
        SignatureRequest: {
            prepare: true,
            action: "prepare",
            mode: "draft",
            source: "matters.firm",
        },
    };
}

function Detail(props) {
    const {
        auth,
        params,
        location,
        navigate,
        onChange,

        pageInfo,
        loading,
        getMatter,
        completeExtract,
        deleteMatter,
        getSettings,
        getClient,
        saveMatter,
        getReports,
        getEmails,
        getLogs,
        saveLog,

        inline,
        render,
        prepareSignature,
        sendEmail,
    } = props;

    const assignmentRef = useRef(null);
    const partiesRef = useRef(null);
    const divorceRef = useRef(null);
    const nameChangeRef = useRef(null);
    const notesRef = useRef(null);
    const agreementEditorRefs = useRef(new Map());
    const matterCaptionRef = useRef("");
    const agreementQueryOpenedRef = useRef(null);

    const isMountedRef = useRef(false);
    const loadingDataRef = useRef(false);
    const importStateRef = useRef(location?.state?.surveyImport || null);
    const clientSeedRef = useRef(location?.state?.clientSeed || null);
    const suppressNavigationGuardRef = useRef(false);

    const uuidRef = useRef(isGuid(params?.uuid) ? params.uuid : uuidv4());
    const uuid = uuidRef.current;
    const isNew = useMemo(() => !isGuid(params?.uuid), [params?.uuid]);

    const [selected, setSelected] = useState("general");
    const [matter, setMatter] = useState({ uuid });
    const [rates, setRates] = useState([]);
    const [minutes, setMinutes] = useState(6);
    const [logs, setLogs] = useState([]);
    const [reports, setReports] = useState([]);
    const [agreementTabs, setAgreementTabs] = useState([]);
    const [changed, setChanged] = useState(false);
    const [pendingNote, setPendingNote] = useState(false);
    const [surveyImport, setSurveyImport] = useState(importStateRef.current);
    const [clientSeed, setClientSeed] = useState(clientSeedRef.current);
    matterCaptionRef.current = matter?.caption || "";

    const [modal, setModal] = useState({
        error: { show: false, title: "", body: "" },
        confirm: { show: false, title: "", body: "", onOk: null },
        report: { show: false },
        extract: { show: false },
        delete: { show: false },
        email: { show: false },
        agreementClose: { show: false, uuid: null, title: "", saving: false },
    });

    const safeSet = useCallback((setter) => {
        if (!isMountedRef.current) return;
        setter();
    }, []);

    const doPostChange = useCallback(() => {
        if (onChange) {
            onChange({
                selected,
                matter,
                rates,
                minutes,
                logs,
                reports,
                modal,
                changed,
                surveyImport,
                clientSeed,
            });
        }
    }, [onChange, selected, matter, rates, minutes, logs, reports, modal, changed, surveyImport, clientSeed]);

    const permitted = useCallback(
        (named) => {
            const names = Array.isArray(named) ? named : [named];
            const permissions = auth?.permissions ?? [];
            return permissions.some((item) => names.includes(item));
        },
        [auth?.permissions]
    );

    const has = useCallback(
        ({ flag, flags }) => {
            return Boolean((flags ?? matter.matterFlags ?? 0) & flag);
        },
        [matter]
    );

    const computeReportFlags = useCallback((matterFlags = 0) => {
        let result = 64;
        if (matterFlags & TABS.DIVORCE) result += 128;
        if (matterFlags & TABS.NAME_CHANGE) result += 8192;
        return result;
    }, []);

    const applySurveyImportToMatter = useCallback((baseMatter = {}) => {
        const importPayload = importStateRef.current;
        if (!importPayload) return baseMatter;

        const next = {
            ...(baseMatter || {}),
            uuid: baseMatter?.uuid ?? uuid,
        };

        if (importPayload?.person?.uuid && !next.clientUuid) {
            next.clientUuid = importPayload.person.uuid;
        }

        if (importPayload?.matterFlags && !next.matterFlags) {
            next.matterFlags = importPayload.matterFlags;
        }

        if (importPayload?.defaultCaption && !next.caption) {
            next.caption = importPayload.defaultCaption;
        }

        if (!next.intakeDate) {
            next.intakeDate = new Date();
        }

        return next;
    }, [uuid]);

    const applyClientSeedToMatter = useCallback((baseMatter = {}) => {
        const seed = clientSeedRef.current;
        if (!seed?.person?.uuid) return baseMatter;

        return {
            ...(baseMatter || {}),
            uuid: baseMatter?.uuid ?? uuid,
            clientUuid: baseMatter?.clientUuid || seed.person.uuid,
            caption: baseMatter?.caption || seed.defaultCaption || `Matter for ${seed.person.displayName || "Client"}`,
            intakeDate: baseMatter?.intakeDate || new Date(),
        };
    }, [uuid]);

    const onErrorShow = useCallback(
        (config = {}) => {
            safeSet(() => {
                setModal((prev) => ({
                    ...prev,
                    error: {
                        ...prev.error,
                        ...config,
                        show: true,
                    },
                }));
            });
        },
        [safeSet]
    );

    const onErrorHideModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                error: {
                    ...prev.error,
                    show: false,
                },
            }));
        });
    }, [safeSet]);

    const onConfirmShow = useCallback(
        (config = {}) => {
            safeSet(() => {
                setModal((prev) => ({
                    ...prev,
                    confirm: {
                        ...prev.confirm,
                        ...config,
                        show: true,
                    },
                }));
            });
        },
        [safeSet]
    );

    const onConfirmHideModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                confirm: {
                    ...prev.confirm,
                    show: false,
                },
            }));
        });
    }, [safeSet]);

    const onReportClick = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                report: { ...prev.report, show: true, working: false, workingLabel: "" },
            }));
        });
    }, [safeSet]);

    const onReportHideModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => {
                if (prev.report?.working) return prev;

                return {
                    ...prev,
                    report: { ...prev.report, show: false },
                };
            });
        });
    }, [safeSet]);

    const closeReportModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                report: { ...prev.report, show: false, working: false, workingLabel: "" },
            }));
        });
    }, [safeSet]);

    const setReportWorking = useCallback((working, workingLabel = "") => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                report: {
                    ...prev.report,
                    working,
                    workingLabel: working ? workingLabel : "",
                },
            }));
        });
    }, [safeSet]);

    const onEmailShow = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                email: { ...prev.email, show: true },
            }));
        });
    }, [safeSet]);

    const onEmailHideModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                email: { ...prev.email, show: false },
            }));
        });
    }, [safeSet]);

    const onExtractShow = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                extract: { show: true },
            }));
        });
    }, [safeSet]);

    const onExtractHideModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                extract: { ...prev.extract, show: false },
            }));
        });
    }, [safeSet]);

    const onDeleteShow = useCallback(() => {
        if (isNew || !isGuid(uuid)) return;

        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                delete: { show: true },
            }));
        });
    }, [isNew, safeSet, uuid]);

    const onDeleteHideModal = useCallback(() => {
        safeSet(() => {
            setModal((prev) => ({
                ...prev,
                delete: { ...prev.delete, show: false },
            }));
        });
    }, [safeSet]);

    const loadData = useCallback(async () => {
        if (loadingDataRef.current) return;
        loadingDataRef.current = true;

        loading(true);

        try {
            if (isNew) {
                const seededMatter = applyClientSeedToMatter(applySurveyImportToMatter({ uuid }));
                const seededClientUuid = seededMatter?.clientUuid;
                let client = null;

                if (seededClientUuid) {
                    client = await getClient({ uuid: seededClientUuid });
                }

                safeSet(() => {
                    setMatter(seededMatter);
                    setSurveyImport(importStateRef.current);
                    setClientSeed(clientSeedRef.current);
                    setRates(Array.isArray(client?.rates) ? [...client.rates] : []);
                    setMinutes(Number.isFinite(client?.minutes) ? client.minutes : 6);
                    setSelected("general");
                    setChanged(false);
                });
                return;
            }

            const matterResp = await getMatter({ uuid });
            const item = matterResp?.item ?? {};
            const client = item?.Client ?? {};
            const clientName = personDisplayName(client?.Person || client) || "Client";
            const logs = await getLogs({ target: uuid });

            const reportFlags = computeReportFlags(item?.matterFlags ?? 0);
            const [reportItems, emailItems, agreementTypeResult] = await Promise.all([
                getReports({ flags: reportFlags }),
                getEmails({ flags: reportFlags }),
                permitted(CREATE_AGREEMENT) ? getAgreementTypes() : Promise.resolve({ items: [] }),
            ]);
            const nextReports = (reportItems || []).map((report) => ({
                ...report,
                style: GENERATOR_STYLES.REPORT,
            }));
            const nextEmails = (emailItems || []).map((email) => ({
                ...email,
                style: GENERATOR_STYLES.EMAIL,
            }));
            const nextAgreements = (agreementTypeResult?.items || []).map((type) => ({
                uuid: `agreement-type:${type.uuid}`,
                caption: type.name,
                name: type.slug,
                category: "Agreements",
                style: GENERATOR_STYLES.AGREEMENT,
                agreementType: type,
            }));

            safeSet(() => {
                const seededMatter = applyClientSeedToMatter(applySurveyImportToMatter({ ...item, uuid: item?.uuid ?? uuid }));

                setMatter(seededMatter);
                setSurveyImport(importStateRef.current);
                setClientSeed(clientSeedRef.current);
                setRates(Array.isArray(client?.rates) ? [...client.rates] : []);
                setMinutes(Number.isFinite(client?.minutes) ? client.minutes : 6);
                setLogs(Array.isArray(logs?.items) ? logs.items : []);
                setReports([...nextEmails, ...nextReports, ...nextAgreements, COMPLETE_EXTRACT_OPTION]);
                setSelected("general");
                setChanged(false);

                pageInfo({
                    title: `Matter (${seededMatter?.caption})`,
                    breadcrumb: `${seededMatter?.caption}`,
                    breadcrumbPath: `/dashboard/matters/${uuid}`,
                    breadcrumbItems: isGuid(seededMatter?.clientUuid)
                        ? [{
                            to: `/dashboard/people/${seededMatter.clientUuid}`,
                            text: clientName,
                        }]
                        : [],
                });
            });
        } catch (err) {
            onErrorShow({
                title: "Failed to load matter",
                body: err?.message || "An unexpected error occurred while loading.",
            });
        } finally {
            loading(false);
            loadingDataRef.current = false;
        }
    }, [
        loading,
        isNew,
        getMatter,
        getReports,
        getEmails,
        permitted,
        getLogs,
        getClient,
        computeReportFlags,
        uuid,
        safeSet,
        onErrorShow,
        pageInfo,
        applySurveyImportToMatter,
        applyClientSeedToMatter,
    ]);

    const onContainedChange = useCallback(
        (named, patch) => {
            safeSet(() => {
                if (named === "matter") {
                    setMatter((prev) => ({ ...(prev || {}), ...(patch || {}) }));
                } else if (named === "modal") {
                    setModal((prev) => ({ ...(prev || {}), ...(patch || {}) }));
                }
                setChanged(!loadingDataRef.current);
            });
        },
        [safeSet]
    );

    const onGeneralChange = useCallback(
        async (patch) => {
            try {
                const nextClientUuid = patch?.clientUuid;
                const currentClientUuid = matter?.clientUuid;

                if (nextClientUuid && nextClientUuid !== currentClientUuid) {
                    const client = await getClient({ uuid: nextClientUuid });
                    safeSet(() => {
                        setRates(Array.isArray(client?.rates) ? [...client.rates] : []);
                        setMinutes(Number.isFinite(client?.minutes) ? client.minutes : 6);
                    });
                }

                onContainedChange("matter", patch);
            } catch (err) {
                onErrorShow({
                    title: "Failed to load client defaults",
                    body: err?.message || "Could not load rates/minutes from selected client.",
                });
            }
        },
        [matter?.clientUuid, getClient, safeSet, onContainedChange, onErrorShow]
    );

    const savePendingNote = useCallback(
        async () => {
            if (!notesRef.current?.hasPendingText?.()) return;

            const record = {
                uuid: uuidv4(),
                target: uuid,
                category: "Note",
                authorUuid: auth.user.uuid,
                code: 0,
                meta: notesRef.current.getPendingText()
            };

            await saveLog(record);
            notesRef.current.clearPendingText();
            safeSet(() => setPendingNote(false));
        },
        [auth?.user?.uuid, saveLog, safeSet, uuid]
    );

    const onSaveClick = useCallback(
        async (e) => {
            e?.preventDefault?.();
            loading(true);

            try {
                await saveMatter({
                    ...(matter || {}),
                    uuid,
                });

                if (assignmentRef.current?.saveChanges) {
                    await assignmentRef.current.saveChanges();
                }

                if (partiesRef.current?.saveChanges) {
                    await partiesRef.current.saveChanges();
                }

                if (has({ flag: TABS.DIVORCE }) && divorceRef.current?.saveChanges) {
                    await divorceRef.current.saveChanges();
                }

                if (has({ flag: TABS.NAME_CHANGE }) && nameChangeRef.current?.saveChanges) {
                    await nameChangeRef.current.saveChanges();
                }

                await savePendingNote();

                await loadData();
                safeSet(() => setChanged(false));
                suppressNavigationGuardRef.current = true;
                window.location.href = `/dashboard/matters/${uuid}`;
            } catch (err) {
                onErrorShow({
                    title: "Save failed",
                    body: err?.message || "An unexpected error occurred while saving.",
                });
            } finally {
                loading(false);
            }
        },
        [loading, saveMatter, matter, uuid, loadData, safeSet, onErrorShow, has, savePendingNote]
    );

    const onSubmit = useCallback(
        (e) => {
            e.preventDefault();
            onSaveClick(e);
        },
        [onSaveClick]
    );

    const openAgreementTab = useCallback((agreementOrUuid) => {
        const agreement = typeof agreementOrUuid === "string"
            ? { uuid: agreementOrUuid }
            : agreementOrUuid || {};
        if (!isGuid(agreement.uuid)) return;

        const key = agreementTabKey(agreement.uuid);
        const title = agreementTabCaption(matter?.caption, agreement);
        safeSet(() => {
            setAgreementTabs((tabs) => {
                const existing = tabs.find((tab) => tab.uuid === agreement.uuid);
                if (existing) {
                    if (!agreement.title && !agreement.name) return tabs;
                    return tabs.map((tab) => tab.uuid === agreement.uuid ? { ...tab, title } : tab);
                }
                return [...tabs, { uuid: agreement.uuid, key, title, dirty: false }];
            });
            setSelected(key);
        });
    }, [matter?.caption, safeSet]);

    useEffect(() => {
        if (!isMountedRef.current || !isGuid(matter?.uuid) || !String(matter?.caption || "").trim()) return;
        const agreementUuid = new URLSearchParams(location?.search || "").get("agreement");
        if (!isGuid(agreementUuid) || agreementQueryOpenedRef.current === agreementUuid) return;
        agreementQueryOpenedRef.current = agreementUuid;
        openAgreementTab(agreementUuid);
    }, [location?.search, matter?.uuid, matter?.caption, openAgreementTab]);

    const onReportOk = useCallback(
        async (e) => {
            let startedWorking = false;
            let failureTitle = "Report generation failed";

            try {
                const report = e?.report?.target?.value;

                if (report?.blank) {
                    closeReportModal();
                    onEmailShow();
                    return;
                }

                if (report?.style === GENERATOR_STYLES.EXTRACT) {
                    closeReportModal();
                    onExtractShow();
                    return;
                }

                if (report?.style === GENERATOR_STYLES.AGREEMENT) {
                    failureTitle = "Agreement creation failed";
                    setReportWorking(true, "Creating agreement...");
                    loading(true);
                    startedWorking = true;
                    const type = report.agreementType;
                    const result = await createAgreement({
                        matterUuid: uuid,
                        agreementTypeUuid: type?.uuid,
                        jurisdiction: type?.jurisdiction,
                    });
                    if (!isGuid(result?.item?.uuid)) throw new Error("The agreement was created, but it could not be opened.");
                    closeReportModal();
                    openAgreementTab(result.item);
                    return;
                }

                const wantsSignatureRequest = e?.signatureRequest === true;
                const workingLabel = wantsSignatureRequest
                    ? "Preparing signature request..."
                    : report?.style === GENERATOR_STYLES.EMAIL
                        ? "Sending email..."
                        : "Generating document...";

                setReportWorking(true, workingLabel);
                loading(true);
                startedWorking = true;

                const firm = await getSettings({ authentic: true, category: "Firm" });
                const firmSettings = templateFirmSettings(firm);

                let parties = [...(partiesRef?.current?.getData() ?? [])];
                if (deepEqual(parties?.[0], {})) parties.shift();

                const dataset = {
                    Firm: firmSettings,
                    Matter: matter,
                    Parties: parties,
                    ...signatureRequestTemplateData(wantsSignatureRequest),
                };

                if (has({ flag: TABS.DIVORCE })) {
                    const sourceDivorce = divorceRef?.current?.getData()?.divorce;

                    if (sourceDivorce) {
                        const divorce = {
                            ...sourceDivorce,
                            Plaintiff: sourceDivorce?.Plaintiff
                                ? { ...sourceDivorce.Plaintiff }
                                : undefined,
                            Defendant: sourceDivorce?.Defendant
                                ? { ...sourceDivorce.Defendant }
                                : undefined,
                            Children: Array.isArray(sourceDivorce?.Children)
                                ? sourceDivorce.Children.map((child) => ({ ...(child || {}) }))
                                : [],
                        };

                        dataset.Divorce = divorce;
                    }
                }

                if (has({ flag: TABS.NAME_CHANGE })) {
                    const sourceNamechange = nameChangeRef?.current?.getData()?.nameChange;

                    if (sourceNamechange) {
                        const nameChange = {
                            ...sourceNamechange
                        };

                        dataset.NameChange = nameChange;
                    }
                }

                const datasetWithCustom = applyCustomValues(dataset, e?.custom, getTemplateCustomFields(report));
                const recipient = matter?.Client?.Person?.email || "";

                if (report?.style === GENERATOR_STYLES.EMAIL) {
                    await sendEmail({
                        name: report.name,
                        json: datasetWithCustom,
                        recipient,
                    });
                    closeReportModal();
                    return;
                }

                if (!inline || !render) {
                    throw new Error("Document rendering services are not available.");
                }

                if (!isGuid(report?.uuid) || !report?.output || !report?.fileName) {
                    throw new Error("Invalid report request.");
                }

                const outputType = renderingStyle(report.output);

                if (!outputType) {
                    throw new Error("Unsupported report output.");
                }

                if (wantsSignatureRequest) {
                    const recipients = matterSignatureRecipients(matter, firmSettings);
                    if (!recipients.some((recipient) => recipient.key === "client")) {
                        throw new Error("The matter client needs an email address before a signature request can be prepared.");
                    }

                    const signatureResult = await prepareSignature({
                        json: datasetWithCustom,
                        uuid: report.uuid,
                        output: report.output,
                        title: report.caption || report.name || report.fileName,
                        matterUuid: uuid,
                        ownerType: "matter",
                        ownerUuid: uuid,
                        contextType: "matter",
                        contextUuid: uuid,
                        personUuid: matter?.Client?.Person?.uuid,
                        ownerPersonUuid: matter?.Client?.Person?.uuid,
                        recipients,
                    });
                    const envelopeUuid = signatureResult?.item?.envelope?.uuid;
                    if (!isGuid(envelopeUuid)) {
                        throw new Error("The signature request was created, but the envelope could not be opened.");
                    }

                    closeReportModal();
                    navigate(`/dashboard/signatures/${envelopeUuid}`);
                    return;
                }

                const reportGenerators = renderingGenerators({ render });
                const generate = reportGenerators[report.output];
                if (!generate) {
                    throw new Error("No report generator is available for the selected output.");
                }

                const result = await generate({
                    json: datasetWithCustom,
                    uuid: report.uuid,
                    output: report.output,
                });

                const resolvedFileName = await renderingFileName({
                    inline,
                    sanitizeFilename,
                    fileName: report.fileName,
                    json: datasetWithCustom,
                    output: report.output,
                });

                renderedDownload({
                    result,
                    fileName: resolvedFileName,
                    mime: outputType.mime,
                });
                closeReportModal();

            } catch (err) {
                onErrorShow({
                    title: failureTitle,
                    body: err?.message || "Unable to generate the selected report.",
                });
            } finally {
                if (startedWorking) {
                    setReportWorking(false);
                    loading(false);
                }
            }
        },
        [
            closeReportModal,
            setReportWorking,
            loading,
            onEmailShow,
            inline,
            render,
            prepareSignature,
            getSettings,
            matter,
            navigate,
            onErrorShow,
            uuid,
            has,
            sendEmail,
            onExtractShow,
            openAgreementTab,
        ]
    );

    const onExtractOk = useCallback(
        async ({ selection }) => {
            try {
                const name = sanitizeFilename(`${matter?.caption || uuid} Complete Extract.zip`);
                await completeExtract({ uuid, name, selection });
            } catch (err) {
                onErrorShow({
                    title: "Export failed",
                    body: err?.message || "Unable to export the selected matter data.",
                });
            } finally {
                onExtractHideModal();
            }
        },
        [completeExtract, matter?.caption, onErrorShow, onExtractHideModal, uuid]
    );

    const onDeleteCompleteExtract = useCallback(
        async () => {
            try {
                const name = sanitizeFilename(`${matter?.caption || uuid} Complete Extract.zip`);
                await completeExtract({ uuid, name });
            } catch (err) {
                onErrorShow({
                    title: "Export failed",
                    body: err?.message || "Unable to export the matter data.",
                });
            }
        },
        [completeExtract, matter?.caption, onErrorShow, uuid]
    );

    const onDeleteOk = useCallback(
        async () => {
            if (!isGuid(uuid)) return;

            loading(true);
            try {
                const result = await deleteMatter({ uuid });
                if (result?.success) {
                    suppressNavigationGuardRef.current = true;
                    onDeleteHideModal();
                    navigate("/dashboard/matters", { replace: true });
                    return;
                }

                onDeleteHideModal();
                onErrorShow({
                    title: "Matter not deleted",
                    body: result?.error?.message || "The matter could not be deleted.",
                });
            } catch (err) {
                onDeleteHideModal();
                onErrorShow({
                    title: "Matter not deleted",
                    body: err?.message || "The matter could not be deleted.",
                });
            } finally {
                loading(false);
            }
        },
        [deleteMatter, loading, navigate, onDeleteHideModal, onErrorShow, uuid]
    );

    const onClickCreateDivorce = useCallback(
        (e) => {
            e?.preventDefault?.();
            onConfirmShow({
                title: "Create divorce matter?",
                body: "Click ok to attach a divorce matter to this case.",
                onOk: async () => {
                    onConfirmHideModal();
                    safeSet(() => {
                        setMatter((prev) => ({
                            ...prev,
                            matterFlags: (prev?.matterFlags ?? 0) | TABS.DIVORCE,
                        }));
                        setSelected("divorce");
                        setChanged(true);
                    });
                },
            });
        },
        [onConfirmShow, onConfirmHideModal, safeSet]
    );

    const onClickDestroyDivorce = useCallback(
        (e) => {
            e?.preventDefault?.();
            onConfirmShow({
                title: "Remove divorce matter information?",
                body: "Click ok to remove the divorce information for this matter.",
                onOk: async () => {
                    onConfirmHideModal();
                    safeSet(() => {
                        setMatter((prev) => ({
                            ...prev,
                            matterFlags: (prev?.matterFlags ?? 0) & ~TABS.DIVORCE,
                        }));
                        setSelected("general");
                        setChanged(true);
                    });
                },
            });
        },
        [onConfirmShow, onConfirmHideModal, safeSet]
    );

    const onClickCreateNameChange = useCallback(
        (e) => {
            e?.preventDefault?.();
            onConfirmShow({
                title: "Create name change matter?",
                body: "Click ok to attach a name change matter to this case.",
                onOk: async () => {
                    onConfirmHideModal();
                    safeSet(() => {
                        setMatter((prev) => ({
                            ...prev,
                            matterFlags: (prev?.matterFlags ?? 0) | TABS.NAME_CHANGE,
                        }));
                        setSelected("nameChange");
                        setChanged(true);
                    });
                },
            });
        },
        [onConfirmShow, onConfirmHideModal, safeSet]
    );

    const onClickDestroyNameChange = useCallback(
        (e) => {
            e?.preventDefault?.();
            onConfirmShow({
                title: "Remove name change matter information?",
                body: "Click ok to remove the name change information for this matter.",
                onOk: async () => {
                    onConfirmHideModal();
                    safeSet(() => {
                        setMatter((prev) => ({
                            ...prev,
                            matterFlags: (prev?.matterFlags ?? 0) & ~TABS.NAME_CHANGE,
                        }));
                        setSelected("general");
                        setChanged(true);
                    });
                },
            });
        },
        [onConfirmShow, onConfirmHideModal, safeSet]
    );

    const onTabChange = useCallback(
        (e, key) => {
            e?.preventDefault?.();
            safeSet(() => setSelected(key));
        },
        [safeSet]
    );

    const loadMatterAgreements = useCallback(() => listAgreements(uuid), [uuid]);

    const onOpenAgreementFromFiles = useCallback((agreementUuid, source = {}) => {
        openAgreementTab(source.agreement || { uuid: agreementUuid, title: source.name });
    }, [openAgreementTab]);

    const onAgreementLoaded = useCallback((item) => {
        if (!isGuid(item?.uuid)) return;
        const title = agreementTabCaption(matterCaptionRef.current, item);
        setAgreementTabs((tabs) => {
            const current = tabs.find((tab) => tab.uuid === item.uuid);
            if (!current || current.title === title) return tabs;
            return tabs.map((tab) => tab.uuid === item.uuid ? { ...tab, title } : tab);
        });
    }, []);

    const onAgreementDirtyChange = useCallback((agreementUuid, dirty) => {
        setAgreementTabs((tabs) => {
            const current = tabs.find((tab) => tab.uuid === agreementUuid);
            if (!current || current.dirty === Boolean(dirty)) return tabs;
            return tabs.map((tab) => tab.uuid === agreementUuid ? { ...tab, dirty: Boolean(dirty) } : tab);
        });
    }, []);

    const registerAgreementEditor = useCallback((agreementUuid, instance) => {
        if (instance) agreementEditorRefs.current.set(agreementUuid, instance);
        else agreementEditorRefs.current.delete(agreementUuid);
    }, []);

    const removeAgreementTab = useCallback((agreementUuid) => {
        const index = agreementTabs.findIndex((tab) => tab.uuid === agreementUuid);
        if (index < 0) return;
        const closingKey = agreementTabKey(agreementUuid);
        const remaining = agreementTabs.filter((tab) => tab.uuid !== agreementUuid);
        agreementEditorRefs.current.delete(agreementUuid);
        setAgreementTabs(remaining);
        if (selected === closingKey) {
            const replacement = remaining[Math.min(index, remaining.length - 1)];
            setSelected(replacement?.key || "files");
        }
        setModal((current) => ({
            ...current,
            agreementClose: { show: false, uuid: null, title: "", saving: false },
        }));
    }, [agreementTabs, selected]);

    const onAgreementCloseRequest = useCallback((agreementUuid) => {
        const tab = agreementTabs.find((item) => item.uuid === agreementUuid);
        if (!tab) return;
        const editorApi = agreementEditorRefs.current.get(agreementUuid);
        if (editorApi?.isBusy?.()) {
            onErrorShow({ title: "Agreement is still working", body: "Wait for the current agreement operation to finish before closing its tab." });
            return;
        }
        if (!(tab.dirty || editorApi?.hasPendingChanges?.())) {
            removeAgreementTab(agreementUuid);
            return;
        }
        setModal((current) => ({
            ...current,
            agreementClose: { show: true, uuid: agreementUuid, title: tab.title, saving: false },
        }));
    }, [agreementTabs, onErrorShow, removeAgreementTab]);

    const onAgreementCloseCancel = useCallback(() => {
        setModal((current) => ({
            ...current,
            agreementClose: { show: false, uuid: null, title: "", saving: false },
        }));
    }, []);

    const onAgreementCloseWithoutSave = useCallback(() => {
        if (isGuid(modal.agreementClose.uuid)) removeAgreementTab(modal.agreementClose.uuid);
    }, [modal.agreementClose.uuid, removeAgreementTab]);

    const onAgreementSaveAndClose = useCallback(async () => {
        const agreementUuid = modal.agreementClose.uuid;
        if (!isGuid(agreementUuid)) return;
        const editorApi = agreementEditorRefs.current.get(agreementUuid);
        setModal((current) => ({
            ...current,
            agreementClose: { ...current.agreementClose, saving: true },
        }));
        const saved = await editorApi?.savePendingChanges?.();
        if (saved) {
            removeAgreementTab(agreementUuid);
            return;
        }
        setModal((current) => ({
            ...current,
            agreementClose: { ...current.agreementClose, saving: false },
        }));
    }, [modal.agreementClose.uuid, removeAgreementTab]);

    const onLogSave = useCallback(
        async (text) => {
            const record = {
                uuid: uuidv4(),
                target: uuid,
                category: "Note",
                authorUuid: auth.user.uuid,
                code: 0,
                meta: text
            };

            await saveLog(record);
            safeSet(() => {
                setLogs((prev) => [record, ...(prev || [])]);
                setChanged(true);
            });
        },
        [safeSet, saveLog, auth?.user?.uuid, uuid]
    );

    const hasUnsavedChanges = Boolean(changed || pendingNote || agreementTabs.some((tab) => tab.dirty));

    const addActions = useMemo(() => {
        const result = [];

        if (isNew) return result;

        if (permitted(EDIT_MATTER) && !has({ flag: TABS.DIVORCE })) {
            result.push({
                caption: "Divorce Case",
                onClick: onClickCreateDivorce,
            });
        }

        if (permitted(EDIT_MATTER) && !has({ flag: TABS.NAME_CHANGE })) {
            result.push({
                caption: "Name Change Case",
                onClick: onClickCreateNameChange,
            });
        }

        return result;
    }, [isNew, permitted, has, onClickCreateDivorce, onClickCreateNameChange]);

    const emailContext = useMemo(() => ({
        selected,
        matter,
        rates,
        minutes,
        logs,
        reports,
        modal,
        changed,
        surveyImport,
        clientSeed,
        client: matter?.Client,
        person: matter?.Client?.Person,
    }), [
        selected,
        matter,
        rates,
        minutes,
        logs,
        reports,
        modal,
        changed,
        surveyImport,
        clientSeed,
    ]);

    const calendarScopeLabel = useMemo(() => {
        return String(matter?.caption || matter?.number || matter?.caseNumber || "Case").replace(/\s+/g, " ").trim() || "Case";
    }, [matter?.caption, matter?.number, matter?.caseNumber]);

    useEffect(() => {
        isMountedRef.current = true;

        pageInfo({
            title: "Matter",
            breadcrumb: isNew ? "[New Matter]" : "[Matter]",
            breadcrumbPath: null,
            breadcrumbItems: [],
        });

        loadData();

        return () => {
            isMountedRef.current = false;
        };
    }, [loading, pageInfo, isNew, loadData]);

    useEffect(() => {
        doPostChange();
    }, [doPostChange]);

    return (
        <>
            <NavigationGuard
                when={() => !suppressNavigationGuardRef.current && hasUnsavedChanges}
                message="This matter or an open agreement has changes that are not saved in a recoverable version. Leave without saving?"
            />
            <Col md="8" className="float-right text-end">
                <ConditionalDropdown
                    enabledVariant="outline-primary"
                    display={addActions.length > 0}
                    enabled
                    icon="clock"
                    items={addActions}
                >
                    Add
                </ConditionalDropdown>
                <ConditionalButton
                    enabledVariant="outline-primary"
                    tooltip="Generate a Document"
                    display={!isNew}
                    enabled={!Boolean(changed)}
                    onEnabledClick={onReportClick}
                    icon="content"
                >
                    Generate
                </ConditionalButton>
                <ConditionalButton
                    enabledVariant="outline-danger"
                    tooltip="Delete matter"
                    display={!isNew}
                    enabled={!Boolean(changed)}
                    onEnabledClick={onDeleteShow}
                    icon="bin"
                >
                    Delete
                </ConditionalButton>
                <ConditionalButton
                    enabledVariant="outline-primary"
                    tooltip="Save Matter"
                    display
                    enabled
                    onEnabledClick={onSaveClick}
                    icon="save"
                >
                    Save
                </ConditionalButton>
            </Col>

            <Tab.Container activeKey={selected}>
                <Card.Header className="app-tab-header border-0 pb-0">
                    <Nav
                        className="app-tab-nav nav-tabs-line card-header-tabs"
                        variant="tabs"
                        activeKey={selected}
                    >
                        <Nav.Item className="text-center">
                            <Nav.Link
                                className={`${selected === "general" ? "active" : ""} app-tab-link`}
                                onClick={(event) => onTabChange(event, "general")}
                            >
                                General
                            </Nav.Link>
                        </Nav.Item>

                        <NavItem
                            name="divorce"
                            caption="Divorce"
                            hidden={!has({ flag: TABS.DIVORCE })}
                            creator={false}
                            created={has({ flag: TABS.DIVORCE })}
                            destroyer={permitted(EDIT_MATTER)}
                            selected={selected}
                            onClick={(e) => onTabChange(e, "divorce")}
                            onDestroy={onClickDestroyDivorce}
                        />

                        <NavItem
                            name="nameChange"
                            caption="Name Change"
                            hidden={!has({ flag: TABS.NAME_CHANGE })}
                            creator={false}
                            created={has({ flag: TABS.NAME_CHANGE })}
                            destroyer={permitted(EDIT_MATTER)}
                            selected={selected}
                            onClick={(e) => onTabChange(e, "nameChange")}
                            onDestroy={onClickDestroyNameChange}
                        />

                        {!isNew ? (
                        <Nav.Item className="text-center">
                            <Nav.Link
                                className={`${selected === "calendar" ? "active" : ""} app-tab-link`}
                                onClick={(event) => onTabChange(event, "calendar")}
                            >
                                Calendar
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                        {!isNew ? (
                        <Nav.Item className="text-center">
                            <Nav.Link
                                className={`${selected === "files" ? "active" : ""} app-tab-link`}
                                onClick={(event) => onTabChange(event, "files")}
                            >
                                Files
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                        {agreementTabs.map((tab) => (
                        <Nav.Item className="text-center agreement-document-tab" key={tab.uuid}>
                            <Nav.Link
                                href={`#${tab.key}`}
                                className={`${selected === tab.key ? "active" : ""} app-tab-link`}
                                onClick={(event) => onTabChange(event, tab.key)}
                                title={`${tab.title}${tab.dirty ? " — changes not saved in a version" : ""}`}
                            >
                                <Destructable
                                    caption={(
                                        <>
                                            <span className="agreement-document-tab-title">{tab.title}</span>
                                            {tab.dirty ? <span className="agreement-document-tab-dirty" aria-label="Changes not saved in a version" /> : null}
                                        </>
                                    )}
                                    tooltip={`Close ${tab.title}`}
                                    onIconClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onAgreementCloseRequest(tab.uuid);
                                    }}
                                />
                            </Nav.Link>
                        </Nav.Item>
                        ))}

                        {!isNew ? (
                        <Nav.Item className="text-center">
                            <Nav.Link
                                className={`${selected === "notes" ? "active" : ""} app-tab-link`}
                                onClick={(event) => onTabChange(event, "notes")}
                            >
                                Notes
                            </Nav.Link>
                        </Nav.Item>
                        ) : null}

                    </Nav>
                </Card.Header>

                <Col md="12">
                    <Form name="frm" action="post" onSubmit={onSubmit}>
                        <Row>
                            <Col>
                                <Tab.Content className="app-tab-content-min h-100">
                                    <Tab.Pane active={selected === "general"} className="h-100 scroll-out">
                                        <Card body className="mb-5">
                                            <General {...matter} onErrorShow={onErrorShow} onChange={onGeneralChange} />
                                        </Card>

                                        <Card body className="mb-5">
                                            <Assignment owner={uuid} rates={rates} minutes={minutes} ref={assignmentRef} />
                                        </Card>

                                        <Card body className="mb-5">
                                            <Parties owner={uuid} rates={rates} minutes={minutes} clientSeed={clientSeed} ref={partiesRef} />
                                        </Card>
                                    </Tab.Pane>

                                    <Tab.Pane active={selected === "divorce"} className="h-100 scroll-out">
                                        <Divorce uuid={uuid} surveyImport={surveyImport} ref={divorceRef} />
                                    </Tab.Pane>

                                    <Tab.Pane active={selected === "nameChange"} className="h-100 scroll-out">
                                        <NameChange uuid={uuid} surveyImport={surveyImport} ref={nameChangeRef} />
                                    </Tab.Pane>

                                    {!isNew ? (
                                    <Tab.Pane active={selected === "calendar"} className="h-100 scroll-out">
                                        {selected === "calendar" ? (
                                            <Card body className="mb-5">
                                                <Calendar
                                                    caseUuid={uuid}
                                                    showAvailabilityControls={false}
                                                    showContextScopeToggle
                                                    contextScopeLabel={calendarScopeLabel}
                                                />
                                            </Card>
                                        ) : null}
                                    </Tab.Pane>
                                    ) : null}
                                    
                                    {!isNew ? (
                                    <Tab.Pane active={selected === "files"} className="h-100 scroll-out">
                                        <Card body className="mt-5">
                                            {isGuid(matter?.clientUuid) ? (
                                                <Files
                                                    ownerType="person"
                                                    ownerUuid={matter.clientUuid}
                                                    contextType="matter"
                                                    contextUuid={uuid}
                                                    loadAgreements={permitted(VIEW_AGREEMENT) ? loadMatterAgreements : undefined}
                                                    onOpenAgreement={permitted(VIEW_AGREEMENT) ? onOpenAgreementFromFiles : undefined}
                                                />
                                            ) : (
                                                <div className="text-muted">
                                                    Select and save a client before adding matter files.
                                                </div>
                                            )}
                                        </Card>
                                    </Tab.Pane>
                                    ) : null}

                                    {!isNew ? (
                                    <Tab.Pane active={selected === "notes"} className="h-100 scroll-out">
                                        <Notes
                                            ref={notesRef}
                                            logs={logs}
                                            onSave={onLogSave}
                                            onPendingChange={setPendingNote}
                                        />
                                    </Tab.Pane>
                                    ) : null}

                                    {!isNew && permitted(VIEW_AGREEMENT) ? agreementTabs.map((tab) => (
                                    <Tab.Pane key={tab.uuid} active={selected === tab.key} className="h-100 scroll-out">
                                        <Agreements
                                            ref={(instance) => registerAgreementEditor(tab.uuid, instance)}
                                            agreementUuid={tab.uuid}
                                            matter={matter}
                                            auth={auth}
                                            permissions={{
                                                create: permitted(CREATE_AGREEMENT),
                                                edit: permitted(EDIT_AGREEMENT),
                                                finalize: permitted(FINALIZE_AGREEMENT),
                                                export: permitted(EXPORT_AGREEMENT),
                                                ai: permitted(CUSTOMIZE_AGREEMENT_AI),
                                                libraryView: permitted(VIEW_AGREEMENT_PROVISION_LIBRARY),
                                                libraryAdd: permitted(ADD_AGREEMENT_PROVISION_LIBRARY),
                                                libraryApprove: permitted(APPROVE_AGREEMENT_PROVISION_LIBRARY),
                                            }}
                                            onAgreementLoaded={onAgreementLoaded}
                                            onDirtyChange={onAgreementDirtyChange}
                                        />
                                    </Tab.Pane>
                                    )) : null}

                                </Tab.Content>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Tab.Container>

            <ReportModal
                show={modal.report.show}
                items={reports}
                onOk={onReportOk}
                onCancel={onReportHideModal}
                working={modal.report.working}
                workingLabel={modal.report.workingLabel}
            />

            <ExtractModal
                show={modal.extract.show}
                matter={matter}
                onOk={onExtractOk}
                onCancel={onExtractHideModal}
            />

            <DeleteMatterModal
                show={modal.delete.show}
                matter={matter}
                onOk={onDeleteOk}
                onCancel={onDeleteHideModal}
                onExtract={onDeleteCompleteExtract}
            />

            <EmailComposeModal
                show={modal.email.show}
                context={emailContext}
                person={matter?.Client?.Person}
                to={matter?.Client?.Person?.email || ""}
                onCancel={onEmailHideModal}
            />

            <ConfirmModal
                show={modal.agreementClose.show}
                size="lg"
                title="Save agreement changes?"
                body={(
                    <>
                        <p className="mb-2">
                            Save the latest changes to <strong>{modal.agreementClose.title}</strong> as a recoverable version before closing this tab?
                        </p>
                        <small className="text-muted">
                            The collaborative draft is synchronized live. Closing without saving leaves the live draft intact, but does not create a recoverable version for these changes.
                        </small>
                    </>
                )}
                buttons={[
                    {
                        caption: modal.agreementClose.saving ? "Saving..." : "Save and close",
                        onEnabledClick: onAgreementSaveAndClose,
                        variant: "primary",
                        display: true,
                        enabled: !modal.agreementClose.saving,
                    },
                    {
                        caption: "Close without saving a version",
                        onEnabledClick: onAgreementCloseWithoutSave,
                        variant: "outline-danger",
                        display: true,
                        enabled: !modal.agreementClose.saving,
                    },
                    {
                        default: true,
                        caption: "Cancel",
                        onEnabledClick: onAgreementCloseCancel,
                        variant: "secondary",
                        display: true,
                        enabled: !modal.agreementClose.saving,
                    },
                ]}
            />

            <ErrorModal
                {...modal.error}
                buttons={[
                    {
                        default: true,
                        caption: "ok",
                        onClick: onErrorHideModal,
                        variant: "secondary",
                    },
                ]}
            />

            <ConfirmModal
                {...modal.confirm}
                buttons={[
                    {
                        caption: "ok",
                        onEnabledClick: modal?.confirm?.onOk,
                        variant: "secondary",
                        display: true,
                        enabled: true,
                    },
                    {
                        default: true,
                        caption: "cancel",
                        onEnabledClick: onConfirmHideModal,
                        variant: "primary",
                        display: true,
                        enabled: true,
                    },
                ]}
            />
        </>
    );
}

Detail.propTypes = {
    auth: PropTypes.object.isRequired,
    params: PropTypes.object,
    location: PropTypes.object,
    onChange: PropTypes.func,

    pageInfo: PropTypes.func.isRequired,
    loading: PropTypes.func.isRequired,
    getMatter: PropTypes.func.isRequired,
    completeExtract: PropTypes.func.isRequired,
    deleteMatter: PropTypes.func.isRequired,
    getSettings: PropTypes.func.isRequired,
    getClient: PropTypes.func.isRequired,
    saveMatter: PropTypes.func.isRequired,
    getReports: PropTypes.func.isRequired,
    getEmails: PropTypes.func.isRequired,
    getLogs: PropTypes.func.isRequired,
    saveLog: PropTypes.func.isRequired,

    inline: PropTypes.func,
    render: PropTypes.func,
    prepareSignature: PropTypes.func,
    sendEmail: PropTypes.func,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default withNavigation(
    connect(
        mapStateToProps,
        {
            pageInfo,
            loading,
            getMatter,
            completeExtract,
            deleteMatter,
            getSettings,
            getClient,
            saveMatter,
            getReports,
            getEmails,
            getLogs,
            saveLog,
            inline,
            render,
            prepareSignature,
            sendEmail,
        },
        null,
        { forwardRef: true }
    )(Detail)
);
